import { useCallback } from 'react'
import { useAdpSessionStore } from './useAdpSessionStore'
import { useAppDispatch } from '@/app/reduxHooks'
import { markValidationDirty } from '@/features/adp/state/validationSlice'

export interface TargetDataPayload {
  [key: string]: unknown
}

interface TargetDataEntry {
  data: TargetDataPayload
  createdAt: number
}

interface NodeTargetWrapper {
  targetData?: TargetDataEntry
}

export function useTargetData() {
  const { getNode, setNode, getAll } = useAdpSessionStore()
  const dispatch = useAppDispatch()

  /**
   * Set target data
   * Only one target should exist → overrides previous target
   */
  const setTargetData = useCallback(
    (nodeId: string, payload: TargetDataPayload) => {
      const all = getAll()

      // Remove existing target from other nodes
      Object.entries(all).forEach(([id, node]) => {
        const n = node as NodeTargetWrapper
        if (n?.targetData && id !== nodeId) {
          setNode(id, { targetData: undefined })
        }
      })

      const entry: TargetDataEntry = {
        data: payload,
        createdAt: Date.now(),
      }

      setNode(nodeId, {
        targetData: entry,
      })
      dispatch(markValidationDirty())
    },
    [dispatch, getAll, setNode]
  )

  /**
   * Update target data for a node. If no entry exists yet, creates one so
   * the write is never silently dropped — earlier behavior (no-op on missing
   * entry) caused saves like column mapping to never reach session storage,
   * which the validator then flagged as "not saved".
   */
  const updateTargetData = useCallback(
    (nodeId: string, payload: TargetDataPayload) => {
      const existing = getNode(nodeId) as NodeTargetWrapper | null

      const nextData: TargetDataPayload = existing?.targetData
        ? { ...existing.targetData.data, ...payload }
        : { ...payload }

      setNode(nodeId, {
        targetData: {
          data: nextData,
          createdAt: existing?.targetData?.createdAt ?? Date.now(),
        },
      })
      dispatch(markValidationDirty())
    },
    [dispatch, getNode, setNode]
  )

  /**
   * Get current active target (global)
   */
  const getTargetData = useCallback((): TargetDataEntry | null => {
    const all = getAll()

    for (const node of Object.values(all)) {
      const entry = (node as NodeTargetWrapper).targetData
      if (entry) return entry
    }

    return null
  }, [getAll])

  /**
   * Get target by nodeId
   */
  const getTargetDataByNodeId = useCallback(
    (nodeId: string): TargetDataEntry | null => {
      const node = getNode(nodeId) as NodeTargetWrapper | null
      return node?.targetData ?? null
    },
    [getNode]
  )

  /**
   * Clear target (optional utility)
   */
  const clearTargetData = useCallback(
    (nodeId: string) => {
      setNode(nodeId, { targetData: undefined })
      dispatch(markValidationDirty())
    },
    [dispatch, setNode]
  )

  return {
    setTargetData,
    updateTargetData,
    getTargetData,
    getTargetDataByNodeId,
    clearTargetData,
  }
}
